import { ErrorCategory, RestError } from '../rest/baseClient';
import { AxiosError } from 'axios';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isRestError(e: RestError | any): e is RestError {
  return (
    typeof e === 'object' &&
    e.category &&
    typeof e.category === 'string' &&
    e.reason &&
    typeof e.reason === 'string' &&
    e.description &&
    typeof e.description === 'string'
  );
}

export function isNoResponseError(e: AxiosError) {
  return e.isAxiosError && !e.response;
}
export function isNetworkError(e: AxiosError) {
  return isNetworkDisconnectedError(e) || isNetworkTimeoutError(e);
}
export function isNetworkDisconnectedError(e: AxiosError) {
  return isNoResponseError(e) && !e.code;
}
export function isNetworkTimeoutError(e: AxiosError) {
  return isNoResponseError(e) && e.code === 'ECONNABORTED';
}

function getErrorText(e: RestError): string {
  switch (e.reason) {
    case 'NoAvailableSurveySlot':
      return "You are unable to launch the survey because you haven't purchased it yet. Please proceed to purchase a survey.";
    case 'InternalError':
      return 'An internal error has occurred.';
    case 'SurveyDelete':
      return 'This survey cannot be deleted. Only Draft surveys can be deleted.';
    case 'SurveyNotFound':
      return 'Survey not found.';
    case 'SurveyPermissionViolation':
      return 'You do not have permissions to edit this survey';
    case 'InvalidCredentials':
      return 'The email or password is incorrect';
    case 'AccountExpired':
      return 'Your account has expired.';
    default:
      return e.description;
  }
}

export enum SurveyErrorReason {
  NoAvailableSurveySlot = 'NoAvailableSurveySlot',
}

enum PaymentErrorReason {
  PaymentGatewayError = 'PaymentGatewayError',
  InvalidPaymentGatewayConfig = 'InvalidPaymentGatewayConfig',
  SurveyOrderNotFound = 'SurveyOrderNotFound',
  InvalidNewSurveyOrderData = 'InvalidNewSurveyOrderData',
  PaymentNotFound = 'PaymentNotFound',
  DuplicatePaymentId = 'DuplicatePaymentId',
  WrongClientId = 'WrongClientId',
  SurveyOrderAlreadyPaid = 'SurveyOrderAlreadyPaid',
  NoAvailableSlotForSurvey = 'NoAvailableSlotForSurvey',
  CreateSurveySlots = 'CreateSurveySlots',
  IncompatiblePanels = 'IncompatiblePanels',
  ClientNotFound = 'ClientNotFound',
  PaymentNonceRequired = 'PaymentNonceRequired',
}

function convertPaymentError(e: RestError): string {
  const prefix = 'Payment error: ';
  const genericMessage = prefix + 'Something went wrong';
  switch (e.reason as PaymentErrorReason) {
    case PaymentErrorReason.ClientNotFound:
      return genericMessage;
    case PaymentErrorReason.CreateSurveySlots:
      return genericMessage;
    case PaymentErrorReason.DuplicatePaymentId:
      return genericMessage;
    case PaymentErrorReason.IncompatiblePanels:
      return genericMessage;
    case PaymentErrorReason.InvalidNewSurveyOrderData:
      return genericMessage;
    case PaymentErrorReason.InvalidPaymentGatewayConfig:
      return genericMessage;
    case PaymentErrorReason.NoAvailableSlotForSurvey:
      return genericMessage;
    case PaymentErrorReason.PaymentGatewayError:
      if (/\bcvv\b/gi.test(e.description)) {
        return prefix + "CVV doesn't match";
      } else if (/\bavs\b/gi.test(e.description)) {
        return prefix + "Postal code doesn't match";
      } else if (/\binsufficient\s+funds\b/gi.test(e.description)) {
        return prefix + 'Insufficient funds';
      } else if (/\blimit\s+exceeded\b/gi.test(e.description)) {
        return prefix + 'Limit exceeded';
      } else if (/\bexpired\s+card\b/gi.test(e.description)) {
        return prefix + 'Expired card';
      } else if (/\binvalid\s+credit\s+card\s+number\b/gi.test(e.description)) {
        return prefix + 'Invalid card number';
      } else if (/\binvalid\s+expiration\s+date\b/gi.test(e.description)) {
        return prefix + 'Invalid expiration date';
      } else {
        return 'Payment gateway error';
      }
    case PaymentErrorReason.PaymentNonceRequired:
      return genericMessage;
    case PaymentErrorReason.PaymentNotFound:
      return genericMessage;
    case PaymentErrorReason.SurveyOrderAlreadyPaid:
      return prefix + 'Survey order is already paid for';
    case PaymentErrorReason.SurveyOrderNotFound:
      return genericMessage;
    case PaymentErrorReason.WrongClientId:
      return genericMessage;
    default:
      return genericMessage;
  }
}

export function getErrorMessage(e: RestError | Error | string): string {
  if (isRestError(e)) {
    switch (e.category) {
      case ErrorCategory.Api:
        return getErrorText(e);
      case ErrorCategory.Backend:
        return getErrorText(e);
      case ErrorCategory.Clients:
        return getErrorText(e);
      case ErrorCategory.Email:
        return getErrorText(e);
      case ErrorCategory.Frontend:
        return getErrorText(e);
      case ErrorCategory.Payments:
        return convertPaymentError(e);
      case ErrorCategory.Surveys:
        return getErrorText(e);
      default:
        return `Unknown ${e.category} error: reason=${e.reason}, "${e.description}"`;
    }
  } else if (e instanceof Error) {
    return e.message;
  } else {
    return e.toString();
  }
}
